import { styled } from 'baseui'
import type { Theme } from 'baseui'
import type { StyleObject } from 'styletron-standard'
import { pulseLighting } from '@/shared/theme/pulseLighting'

interface ThemeProps {
  $theme: Theme
}

export const pulseOutline = (color: string, style = 'solid'): StyleObject => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: style as StyleObject['borderTopStyle'],
  borderBottomStyle: style as StyleObject['borderBottomStyle'],
  borderLeftStyle: style as StyleObject['borderLeftStyle'],
  borderRightStyle: style as StyleObject['borderRightStyle'],
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

export const pulseRadius = (radius: string): StyleObject => ({
  borderTopLeftRadius: radius,
  borderTopRightRadius: radius,
  borderBottomLeftRadius: radius,
  borderBottomRightRadius: radius,
})

export const OverviewLayout = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: '16px',
})

export const OverviewAside = styled('aside', {
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: '280px',
  maxWidth: '300px',
  minWidth: 0,
})

export const OverviewMain = styled('div', {
  flexGrow: 999,
  flexShrink: 1,
  flexBasis: '640px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
})

export const OverviewCard = styled('section', ({ $theme }: ThemeProps) => ({
  backgroundColor: $theme.colors.white,
  ...pulseOutline($theme.colors.borderPulseCard),
  ...pulseRadius('16px'),
  paddingTop: '16px',
  paddingBottom: '22px',
  paddingLeft: '22px',
  paddingRight: '22px',
  minWidth: 0,
}))

export const SearchForm = styled('form', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px 16px',
})

export const SearchGrid = styled('div', {
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: '520px',
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '12px 16px',
})

export const searchButtonOverrides = {
  BaseButton: {
    style: {
      height: '36px',
      flexShrink: 0,
      gap: '6px',
    },
  },
}

export const CardHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  minHeight: '32px',
})

export const CardTitle = styled('h2', ({ $theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexGrow: 1,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  fontSize: '15px',
  lineHeight: '20px',
  fontWeight: 600,
  color: $theme.colors.textPulseStrong,
}))

export const CardTitleIcon = styled('span', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  color: $theme.colors.textPulseLabel,
}))

export const CountChip = styled<'span', { $hidden: boolean }>('span', ({ $theme, $hidden }) => ({
  display: $hidden ? 'none' : 'inline-flex',
  alignItems: 'center',
  height: '22px',
  paddingLeft: '8px',
  paddingRight: '8px',
  ...pulseRadius('9999px'),
  backgroundColor: $theme.colors.bgPulseSelected,
  color: $theme.colors.primaryPulse,
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}))

export const GhostIconButton = styled('button', ({ $theme }: ThemeProps) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  ...pulseOutline('transparent'),
  ...pulseRadius('8px'),
  backgroundColor: 'transparent',
  color: $theme.colors.primaryPulse,
  cursor: 'pointer',
  ':hover': { backgroundColor: $theme.colors.bgPulseCanvas },
  ':focus-visible': { boxShadow: pulseLighting($theme).shadowPulseFocus },
  ':disabled': { opacity: 0.5, cursor: 'default', backgroundColor: 'transparent' },
}))
